import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Guid } from "@/Common/Models/Guid";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { MedicalItemApiClient } from "@/Patient/Api/MedicalItemApiClient";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemProblemAndCondition, QualifiedDetail } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";

class PatientMedicalItemsHelper {
    public static GetEmptyMedicalProblemItem(encounterId?: Guid): MedicalItemProblemAndCondition {
        let newItem = new MedicalItemProblemAndCondition();
        PatientMedicalItemsHelper.InitializeEmptyMedicalItem(newItem, MedicalItemTypeEnum.PastAndOngoingProblemsAndConditions, encounterId);

        let qualifiedDetail = new QualifiedDetail();
        qualifiedDetail.Id = GuidHelper.Empty();
        qualifiedDetail.tmpIdForFrontEndOnly = sessionStore.newId();

        newItem.QualifiedDetails = [];
        newItem.QualifiedDetails.push(qualifiedDetail);

        return newItem;
    }

    public static GetEmptyMedicalReactionItem(encounterId?: Guid): MedicalItemMedicalReaction {
        var newItem = new MedicalItemMedicalReaction();
        PatientMedicalItemsHelper.InitializeEmptyMedicalItem(newItem, MedicalItemTypeEnum.MedicalReaction, encounterId);
        return newItem;
    }

    public static GetEmptyRiskFactorItem(encounterId?: Guid): MedicalItemRiskFactor {
        var newItem = new MedicalItemRiskFactor();
        PatientMedicalItemsHelper.InitializeEmptyMedicalItem(newItem, MedicalItemTypeEnum.SocialHabitsAndRiskFactors, encounterId);
        return newItem;
    }

    public static GetEmptyAlertItem(encounterId?: Guid): MedicalItemAlertsAndSpecialNeeds {
        var newItem = new MedicalItemAlertsAndSpecialNeeds();
        PatientMedicalItemsHelper.InitializeEmptyMedicalItem(newItem, MedicalItemTypeEnum.AlertsAndSpecialNeeds, encounterId);
        return newItem;
    }

    public static GetEmptyFamilyHistoryItem(encounterId?: Guid): MedicalItemFamilyHistory {
        var newItem = new MedicalItemFamilyHistory();
        PatientMedicalItemsHelper.InitializeEmptyMedicalItem(newItem, MedicalItemTypeEnum.FamilyHistory, encounterId);
        return newItem;
    }

    public static GetEmptyImmunizationItem(encounterId?: Guid): MedicalItemImmunization {
        return MedicalItemImmunization.newImmunization(encounterId);
    }

    public static GetEmptyVitalSignItem(encounterId?: Guid): MedicalItemVitalSign {
        var newItem = new MedicalItemVitalSign();
        PatientMedicalItemsHelper.InitializeEmptyMedicalItem(newItem, MedicalItemTypeEnum.VitalSigns, encounterId);
        return newItem;
    }

    public static InitializeEmptyMedicalItem(newItem: MedicalItem, itemType: MedicalItemTypeEnum, encounterId?: Guid): void {
        newItem.EMRTemplateId = GuidHelper.Empty();
        newItem.displayId = sessionStore.newId().toString();
        newItem.EMRTemplateMedicalItemType = itemType;
        newItem.EMRTemplateIsInvalid = false;
        newItem.EMRTemplateIsVisibleInCPP = true;
        newItem.CommitDate = new Date();
        newItem.IsNewMedicalItem = true;
        newItem.RelatedEncounterId = encounterId;
        newItem.EmployeeId = sessionStore.getCurrentEmployee().ContactFacetId;
        newItem.MedicalItemId = GuidHelper.Empty();
        newItem.Id = newItem.MedicalItemId;
        newItem.IsCommitted = false;
        newItem.IsNewEMRTemplate = true;
    }

    public static GetEmptyMedicalItemActiveMedication(encounterId?: Guid): MedicalItemMedicationAndPrescription {
        const newItem = new MedicalItemMedicationAndPrescription();
        PatientMedicalItemsHelper.InitializeEmptyMedicalItem(newItem, MedicalItemTypeEnum.MedicationsAndPrescriptions, encounterId);
        return newItem;
    }

    public static getMedicalItemCopy(original: MedicalItem, emrTemplateId: string, encounterId?: Guid): MedicalItem {
        const newItem: MedicalItem = JSON.parse(JSON.stringify(original));

        newItem.Id = GuidHelper.Empty();
        newItem.MedicalItemId = GuidHelper.Empty();
        newItem.displayId = sessionStore.newId().toString(); // A copy does not contain an Id since it does not exist in DB yet. Use this instead to manage collapse and other stuff.
        newItem.EMRTemplateId = emrTemplateId;
        newItem.EMRTemplateIsInvalid = false;
        newItem.CommitDate = new Date();
        newItem.RelatedEncounterId = encounterId;
        newItem.EmployeeId = sessionStore.getCurrentEmployee().ContactFacetId;
        newItem.IsNewMedicalItem = true;
        newItem.IsCommitted = false;
        newItem.IsCollapsed = false;
        newItem.SigningEmployeeEmployeeId = null;
        newItem.PharmaStartDate = original.PharmaStartDate;

        if (newItem.EMRTemplateMedicalItemType == MedicalItemTypeEnum.PastAndOngoingProblemsAndConditions) {
            let problemItem = newItem as MedicalItemProblemAndCondition;
            if (problemItem.QualifiedDetails != null && problemItem.QualifiedDetails.length > 0) {
                problemItem.QualifiedDetails.forEach(x => {
                    x.Id = GuidHelper.Empty(); // New Id to Generate Back end
                    x.tmpIdForFrontEndOnly = sessionStore.newId();
                });
            }
        }

        return newItem;
    }

    public static GetLifeStageDateString(date: Date, precision: LifeStageDatePrecisionEnum, fromSummaryPanel: boolean = false): string {
        var newText = "";

        if (date != null) {
            if (precision == LifeStageDatePrecisionEnum.Year)
                newText = LuxonDateTimeParser.parse(date).toFormat("yyyy");
            else if (precision == LifeStageDatePrecisionEnum.MonthYear)
                newText = LuxonDateTimeParser.parse(date).toFormat("yyyy-MM");
            else
                newText = fromSummaryPanel ?
                    LuxonDateTimeParser.parse(date).toFormat("yy/MM/dd") :
                    LuxonDateTimeParser.parse(date).toFormat("yyyy-MM-dd");
        }
        return newText;
    }

    public static GetMedicationNarcoticFrequency(currentDosage: { DosageServedDays?: string }): string {
        return (currentDosage.DosageServedDays != null && currentDosage.DosageServedDays != "") ? cacheStore.GetString("ServedEveryLabel") + " " + currentDosage.DosageServedDays + cacheStore.GetString("DaysLabel").substr(0, 1).toLowerCase() : "";
    }

    public static GetMedicationNarcoticQuantity(currentDosage: { DosageServedFrequency?: string }, dosageUnit: string): string {
        if (currentDosage.DosageServedFrequency != null && currentDosage.DosageServedFrequency != "") {
            const currentDosageUnit = dosageUnit ? (dosageUnit + " ") : "";
            return currentDosage.DosageServedFrequency + " " + currentDosageUnit + cacheStore.GetString("AtATimeLabel");
        }

        return "";
    }

    public static getLatestSavedMedicalItemForEMRTemplate(medicalItems: MedicalItem[]): MedicalItem {
        let realMedicalItems = medicalItems.filter(x => !IsNullOrWhiteSpace(x.Id) && x.Id != GuidHelper.Empty());

        let latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(realMedicalItems).firstOrDefault();
        if (latestMedicalItem)
            return latestMedicalItem;

        return null;
    }

    public static getLatestMedicalItemSavedForEMRTemplate<T extends MedicalItem>(medicalItems: T[]): T {
        return PatientMedicalItemsHelper.getLatestSavedMedicalItemForEMRTemplate(medicalItems) as T;
    }

    public static getLatestMedicalItemIdForEMRTemplate<T extends MedicalItem>(medicalItems: T[]): string {
        let latestMedicalItem = this.getLatestMedicalItemSavedForEMRTemplate(medicalItems);
        if (latestMedicalItem)
            return latestMedicalItem.Id;

        return null;
    }

    public static async setOutsideNoteEncounter(medicalItems?: MedicalItem[], consultationContextLookupId?: string): Promise<string> {
        if (!sessionStore.getCurrentEmployeeDefaultSpecializationLookupId())
            return;

        const encounterId = await MedicalItemApiClient.setOutsideMedicalItemEncounterIds(patientStore.getCurrentPatient(), medicalItems, consultationContextLookupId);
        await new Promise(resolve => {
            patientStore.ActionsMedicalNote.LoadEncounterForView(encounterId, patientStore.getCurrentPatientContactId(), SubServerOperations.MedicalNote.ForGrowthChart, false, () => resolve(null));
        });

        if (SecondScreenNoteService.getInstance().isChildWindowOpen())
            SecondScreenNoteService.getInstance().updateModel(new SecondScreenNoteData({
                patientId: patientStore.getCurrentPatientContactId(),
                encounterId: encounterId
            }));

        this.setPatientMedicalNoteEncounter(encounterId);

        return encounterId;
    }

    public static setPatientMedicalNoteEncounter(encounterId: string): void {
        const patientId = patientStore.getCurrentPatient().IndividualResponse.Contact_id;
        const summaryModel = patientStore.PatientMedicalSummaryModels[patientId];
        const encounters = summaryModel.get("Encounters").toArray();
        const currentEncounter = encounters.firstOrDefault(x => x.Id == encounterId);
        let notesModel = patientStore.PatientMedicalNoteModels[patientId];
        patientStore.PatientMedicalNoteModels[patientId] = notesModel.set("Encounter", currentEncounter);
    }

    /**
     * Convert a string to number if having truthy value.
     * @param value A string to convert.
     * @returns A number converted by a string.
     */
    public static parseLifeStageAgeAtOnsetValue(value: string): number {
        return value ? Number(value) : undefined;
    }
}

export { PatientMedicalItemsHelper };
